import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@material-ui/core';
import { Table, TableColumn } from '@backstage/core-components';
import AddIcon from '@material-ui/icons/Add';
import { StorageLocation, MonitoringEndpoint } from '../../types';

interface SettingsViewProps {
  storageLocations: StorageLocation[];
  monitoringEndpoints: MonitoringEndpoint[];
  systemConfig: any;
  onRefresh: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index } = props;
  return value === index ? <Box p={3}>{children}</Box> : null;
}

export const SettingsView: React.FC<SettingsViewProps> = ({
  storageLocations,
  monitoringEndpoints,
  systemConfig,
}) => {
  const [tabValue, setTabValue] = useState(0);

  const storageColumns: TableColumn[] = [
    { title: 'Name', field: 'metadata.name', highlight: true },
    { title: 'Type', field: 'spec.type' },
    { title: 'Bucket', field: 'spec.bucketName' },
    { title: 'Region', field: 'spec.region' },
    {
      title: 'Used By',
      field: 'status.usedBy',
      render: (row: any) =>
        row.status?.usedBy ? row.status.usedBy.join(', ') : 'None',
    },
  ];

  const monitoringColumns: TableColumn[] = [
    { title: 'Name', field: 'metadata.name', highlight: true },
    { title: 'Type', field: 'spec.type' },
    { title: 'URL', field: 'spec.url' },
    {
      title: 'Namespaces',
      field: 'spec.allowedNamespaces',
      render: (row: any) =>
        row.spec.allowedNamespaces
          ? row.spec.allowedNamespaces.join(', ')
          : 'All',
    },
    {
      title: 'Used By',
      field: 'status.usedBy',
      render: (row: any) =>
        row.status?.usedBy ? row.status.usedBy.join(', ') : 'None',
    },
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Settings
      </Typography>

      <Tabs
        value={tabValue}
        onChange={(_e, newValue) => setTabValue(newValue)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Storage Locations" />
        <Tab label="Monitoring" />
        <Tab label="System" />
      </Tabs>

      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}>
            Add Storage Location
          </Button>
        </Box>
        <Table
          title="Storage Locations"
          options={{ paging: true, pageSize: 5, search: false }}
          columns={storageColumns}
          data={storageLocations}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}>
            Add Monitoring Endpoint
          </Button>
        </Box>
        <Table
          title="Monitoring Endpoints"
          options={{ paging: true, pageSize: 5, search: false }}
          columns={monitoringColumns}
          data={monitoringEndpoints}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Information
                </Typography>
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    OpenEverest Version
                  </Typography>
                  <Typography variant="body1">
                    {systemConfig?.version || 'Unknown'}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    Kubernetes Version
                  </Typography>
                  <Typography variant="body1">
                    {systemConfig?.k8sVersion || 'Unknown'}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    Managed Namespaces
                  </Typography>
                  <Box mt={1}>
                    {systemConfig?.namespaces?.map((ns: string) => (
                      <Chip
                        key={ns}
                        label={ns}
                        size="small"
                        style={{ margin: '2px' }}
                      />
                    ))}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Database Operators
                </Typography>
                {systemConfig?.operators && (
                  <>
                    {systemConfig.operators.postgresql && (
                      <Box mt={2}>
                        <Typography variant="body2" color="textSecondary">
                          PostgreSQL Operator
                        </Typography>
                        <Typography variant="body1">
                          v{systemConfig.operators.postgresql.version}
                          <Chip
                            label={systemConfig.operators.postgresql.status}
                            color="primary"
                            size="small"
                            style={{ marginLeft: '8px' }}
                          />
                        </Typography>
                      </Box>
                    )}
                    {systemConfig.operators.mysql && (
                      <Box mt={2}>
                        <Typography variant="body2" color="textSecondary">
                          MySQL Operator
                        </Typography>
                        <Typography variant="body1">
                          v{systemConfig.operators.mysql.version}
                          <Chip
                            label={systemConfig.operators.mysql.status}
                            color="primary"
                            size="small"
                            style={{ marginLeft: '8px' }}
                          />
                        </Typography>
                      </Box>
                    )}
                    {systemConfig.operators.mongodb && (
                      <Box mt={2}>
                        <Typography variant="body2" color="textSecondary">
                          MongoDB Operator
                        </Typography>
                        <Typography variant="body1">
                          v{systemConfig.operators.mongodb.version}
                          <Chip
                            label={systemConfig.operators.mongodb.status}
                            color="primary"
                            size="small"
                            style={{ marginLeft: '8px' }}
                          />
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};
